import {
  ButtonHierarchy,
  ButtonType,
  MainButton,
  Typography,
  TypographySize,
  TypographyWeight,
} from '@beydesign/storybook';
import { WarningCircle } from '@phosphor-icons/react';

import { paths } from '@/config/paths';

type ErrorPageProps = {
  title?: string;
  description?: string;
  error?: unknown;
  onRetry?: () => void;
};

const getErrorDetails = (error: unknown) => {
  if (error instanceof Error) {
    return error.stack ?? `${error.name}: ${error.message}`;
  }

  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
};

export const ErrorPage = ({
  title = 'Something went wrong',
  description = 'An unexpected error occurred. You can try again or head back to your dashboard.',
  error,
  onRetry,
}: ErrorPageProps) => {
  const handleRetry = onRetry ?? (() => window.location.reload());
  const goToDashboard = () =>
    window.location.assign(
      `${window.location.origin}${paths.app.dashboard.getHref()}`,
    );

  return (
    <div
      role="alert"
      className="flex size-full min-h-[360px] flex-col items-center justify-center gap-6 py-12"
    >
      <div className="flex flex-col items-center gap-4">
        <div className="flex size-[120px] items-center justify-center rounded-full bg-[var(--color-background-bg-page)]">
          <WarningCircle
            className="size-14 text-[var(--color-text-text-subtle)]"
            weight="regular"
          />
        </div>
        <div className="flex max-w-[420px] flex-col items-center gap-1 text-center">
          <Typography
            text={title}
            size={TypographySize.HeadingS}
            weight={TypographyWeight.SemiBold}
            color="var(--color-text-text-title)"
          />
          <Typography
            text={description}
            size={TypographySize.TextS}
            weight={TypographyWeight.Regular}
            color="var(--color-text-text-paragraph)"
          />
        </div>
      </div>

      {import.meta.env.DEV && error != null && (
        <pre className="max-h-[40vh] max-w-full overflow-auto whitespace-pre-wrap rounded-md bg-[var(--color-background-bg-page)] p-4 text-left text-xs text-[var(--color-text-text-paragraph)]">
          {getErrorDetails(error)}
        </pre>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <MainButton
          text="Try again"
          type={ButtonType.Button}
          onClick={handleRetry}
        />
        <MainButton
          text="Go to dashboard"
          type={ButtonType.Button}
          hierarchy={ButtonHierarchy.Secondary}
          onClick={goToDashboard}
        />
      </div>
    </div>
  );
};
